"use client";

import { ArrowRight, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Aithreatdetectionhero() {
  const eyebrow = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const heading = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.1
  });
  const description = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.1
  });
  const ctas = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const image = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const stats = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="142" id="aithreatdetectionhero" className="relative overflow-hidden bg-background text-foreground">
      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-[600px] w-[600px] rounded-lg bg-blue-500/20 blur-[120px]" />
        <div className="absolute top-1/2 -left-40 h-[500px] w-[500px] rounded-lg bg-blue-600/15 blur-[120px]" />
        <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: "linear-gradient(to right, var(--foreground) 1px, transparent 1px), linear-gradient(to bottom, var(--foreground) 1px, transparent 1px)",
        backgroundSize: "60px 60px"
      }} />
      </div>

      {}
      <div className="pointer-events-none absolute top-0 right-0 hidden h-full w-1/2 lg:block">
        <div className="absolute top-0 right-0 h-full w-px bg-gradient-to-b from-transparent via-blue-500/40 to-transparent" style={{
        transform: "rotate(12deg) translateY(-10%)"
      }} />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-8 lg:py-32">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          {}
          <div className="lg:col-span-7">
            <div ref={eyebrow.ref} className={`mb-8 transition-all duration-700 ease-out ${eyebrow.isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
              <Badge variant="outline" className="font-default border-border bg-card/50 px-3 py-1.5 text-xs uppercase tracking-widest backdrop-blur">
                <Zap className="size-3 text-blue-600" />
                <span className="text-foreground">Advanced Cyber Defense</span>
              </Badge>
            </div>

            <h1 ref={heading.ref} className={`font-default text-5xl font-bold leading-[0.95] tracking-tight text-foreground sm:text-6xl md:text-7xl lg:text-8xl transition-all delay-100 duration-700 ease-out ${heading.isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
              AI-Powered
              <br />
              Cybersecurity
              <br />
              <span className="bg-clip-text text-transparent" style={{
              backgroundImage: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 50%, #1e40af 100%)"
            }}>
                for Business.
              </span>
            </h1>

            <p ref={description.ref} className={`font-default mt-8 max-w-xl text-base text-muted-foreground sm:text-lg transition-all delay-200 duration-700 ease-out ${description.isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
              InfinitySecure protects your digital assets with advanced AI-driven threat detection, 24/7 monitoring, and proactive risk management. Stay secure in an evolving digital landscape.
            </p>

            <div ref={ctas.ref} className={`mt-10 flex flex-col gap-4 sm:flex-row sm:items-center transition-all delay-300 duration-700 ease-out ${ctas.isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
              <Link to="/ContactPage">
                <Button size="lg" className="group relative h-14 overflow-hidden px-8 text-base font-semibold shadow-lg shadow-blue-600/30 transition-all hover:shadow-xl hover:shadow-blue-600/50 bg-blue-600 text-white">
                  <span className="relative z-10 flex items-center gap-2">
                    Get Free Assessment
                    <ArrowRight className="size-5 transition-transform group-hover:translate-x-1" />
                  </span>
                  <span className="absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100 bg-gradient-to-br from-blue-500 to-blue-700" />
                </Button>
              </Link>

              <Link to="/ContactPage">
                <Button variant="outline" size="lg" className="h-14 border-border bg-card/30 px-8 text-base font-semibold text-foreground backdrop-blur hover:bg-accent hover:text-accent-foreground">
                  Contact Us
                </Button>
              </Link>
            </div>

            {}
            <div ref={stats.ref} className={`mt-16 grid grid-cols-3 gap-6 border-t border-border pt-8 transition-all delay-500 duration-700 ease-out ${stats.isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
              {[{
              value: "24/7",
              label: "Active Monitoring"
            }, {
              value: "AI",
              label: "Threat Detection"
            }, {
              value: "100%",
              label: "Proactive Defense"
            }].map((stat, i) => <div data-index={i} key={i}>
                  <div className="font-default text-3xl font-bold text-foreground sm:text-4xl">
                    {stat.value}
                  </div>
                  <div className="font-default mt-1 text-xs uppercase tracking-wider text-muted-foreground">
                    {stat.label}
                  </div>
                </div>)}
            </div>
          </div>

          {}
          <div className="lg:col-span-5">
            <div ref={image.ref} className={`relative transition-all delay-200 duration-1000 ease-out ${image.isVisible ? "translate-y-0 rotate-0 opacity-100" : "translate-y-12 -rotate-6 opacity-0"}`}>
              {}
              <div className="absolute inset-0 -m-12">
                <div className="absolute inset-0 rounded-lg blur-3xl" style={{
                background: "radial-gradient(circle at center, #2563eb 0%, transparent 60%)",
                opacity: 0.35
              }} />
                <div className="absolute inset-0 rounded-lg blur-3xl" style={{
                background: "radial-gradient(circle at 70% 30%, #1d4ed8 0%, transparent 50%)",
                opacity: 0.25
              }} />
              </div>

              {}
              <div className="relative" style={{
              transform: "rotate(6deg)"
            }}>
                <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-2xl">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/cyber_security_dashboard.webp" alt="AI-powered cybersecurity threat detection dashboard" loading="lazy" decoding="async" className="aspect-[4/5] w-full object-cover" />
                  {}
                  <div className="pointer-events-none absolute inset-0" style={{
                  background: "linear-gradient(135deg, transparent 0%, rgba(37, 99, 235, 0.1) 100%)"
                }} />
                </div>

                {}
                <div className="absolute -top-4 -left-4 rounded-lg border border-border bg-card/95 px-4 py-3 shadow-xl backdrop-blur" style={{
                transform: "rotate(-6deg)"
              }}>
                  <div className="font-default text-[10px] uppercase tracking-widest text-muted-foreground">
                    Status
                  </div>
                  <div className="font-default mt-0.5 text-sm font-bold text-foreground">
                    System Secure
                  </div>
                </div>

                {}
                <div className="absolute -bottom-4 -right-4 flex items-center gap-3 rounded-lg border border-border bg-card/95 px-4 py-3 shadow-xl backdrop-blur" style={{
                transform: "rotate(-6deg)"
              }}>
                  <div className="flex size-10 items-center justify-center rounded-lg bg-blue-600">
                    <Zap className="size-5 text-white" />
                  </div>
                  <div>
                    <div className="font-default text-sm font-bold text-foreground">
                      Real-time
                    </div>
                    <div className="font-default text-[10px] uppercase tracking-widest text-muted-foreground">
                      Threat Response
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}